/**
 * Product Inventory / Packaging Types
 *
 * Zod schemas and types for Packaging and Other steps of the Add Product form.
 * Sections: Primary Package, Carton/Case, Storage/Expiration, Identifiers,
 *           Other Compounds, Review/Similar Products.
 */
import { z } from 'zod';
import { optionalDecimal, optionalString } from './product-basic-info';

// -- Packaging --------------------------------------------------

export const PrimaryPackageSchema = z.object({
  two_d_barcode: optionalString,
  unit_barcode: optionalString,
  unit_form: optionalString,
  unit_volume: z.string().min(1, 'Volume is required').regex(/^\d*\.?\d+$/, 'Volume must be a positive number'),
  volume_uom: z.string().min(1, 'Volume UOM is required'),
});

export type PrimaryPackageFormValues = z.infer<typeof PrimaryPackageSchema>;

export const CartonCaseSchema = z.object({
  carton_barcode: optionalString,
  carton_quantity_within_case: optionalString,
  case_barcode: optionalString,
  total_primary_units_in_carton: optionalString,
  total_primary_units_in_case: optionalString,
});

// -- Storage ------------------------------------------------------
// Expiration (expiration_after_mixing / expiration_after_opening) moved to
// the Overview step's ExpirationSchema — see product-basic-info.ts.

export const StorageSchema = z.object({
  storage_opened: optionalString,
  storage_unopened: optionalString,
});

export const PackagingSchema = z.object({
  primary_package: PrimaryPackageSchema,
  carton_case: CartonCaseSchema,
  storage: StorageSchema,
});

export type PackagingFormValues = z.infer<typeof PackagingSchema>;

// -- Other ------------------------------------------------------

export const IdentifiersSchema = z.object({
  over_arching_number_nutrition_source: optionalDecimal,
  over_arching_number_pens: optionalDecimal,
  over_arching_number_tmnp: optionalDecimal,
});

export const OtherCompoundsSchema = z.object({
  lutein: optionalDecimal,
  mannitol: optionalDecimal,
  sodium_metabisulfite: optionalDecimal,
});

export const ReviewSimilarProductsSchema = z.object({
  needs_review: optionalString,
  similar_products: optionalString,
  similarity_details: optionalString,
});

export const OtherSchema = z.object({
  identifiers: IdentifiersSchema,
  other_compounds: OtherCompoundsSchema,
  review_similar_products: ReviewSimilarProductsSchema,
});

export type OtherFormValues = z.infer<typeof OtherSchema>;
